import { NutriValues } from "../data/nutriValues";
import { KcalFormLabel, NutriBar, NutriBullets } from "./NutriBar";
import React from "react";
import { MealSourceDto } from "../api";
import { differenceInDays, parseDate, today } from "../utils/dateUtils";
import { Plan2PlateIcon } from "./Plan2PlateIcon";
import { DISH_TAG_LABELS, DishTag } from "../data/dishModification";
import { Badge } from "./ui/Badge";
import { plan2plateIcons } from "../utils/iconUtils";
import { Icon } from "./ui/Icon";

const DISH_TAG_KEYS = new Set(Object.keys(DISH_TAG_LABELS));

export function DetailSearchResult(props: {
  tags?: string[];
  extraTags?: string[];
  title: string;
  resultType: "Mahlzeit" | "Lebensmittel" | "Gericht" | "Produkt";
  recent: boolean;
  purchaseRelevant?: boolean;
  nutriValues?: NutriValues;
  showNumbers: boolean;
  onClick: () => void | Promise<void>;
  onEdit?: () => void | Promise<void>;
}) {
  const hasAnyTags =
    (props.extraTags?.length ?? 0) > 0 || (props.tags?.length ?? 0) > 0;

  return (
    <div
      data-cy="detail-search-result"
      onClick={props.onClick}
      className="cursor-pointer rounded-card border border-line bg-surface-card shadow-card px-4 pb-4 pt-2"
    >
      <div className="flex w-full flex-col items-stretch gap-2">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1 text-ink">{props.title}</div>
          {props.onEdit && (
            <button
              type="button"
              data-cy="result-edit-button"
              aria-label={`${props.title} bearbeiten`}
              onClick={(e) => {
                e.stopPropagation();
                props.onEdit?.();
              }}
              className="shrink-0 rounded-control text-ink-muted hover:bg-surface hover:text-ink leading-[initial]"
            >
              <Icon size={32}>{plan2plateIcons.pencil}</Icon>
            </button>
          )}
        </div>
        <div className="flex flex-nowrap items-center gap-1 overflow-x-auto scroll-container">
          <Badge variant="default" className="shrink-0 border-line">
            {props.resultType}
          </Badge>
          {props.purchaseRelevant && (
            <span
              className="shrink-0 rounded-xs bg-teal/15 px-1.5 py-0.5 text-2xs font-bold text-teal"
              title="Einkaufsrelevant"
            >
              EK
            </span>
          )}
          {props.recent && <Plan2PlateIcon icon="history" size={"sm"} />}
          {hasAnyTags && (
            <span className="shrink-0 text-xs text-ink-muted px-0.5">|</span>
          )}
          {props.extraTags?.map((tag) => (
            <Badge
              key={tag}
              variant="muted"
              className="shrink-0 border-gold bg-gold/10"
            >
              {DISH_TAG_LABELS[tag as DishTag]}
            </Badge>
          ))}
          {!!props.extraTags?.length && (
            <span className="shrink-0 text-xs text-ink-muted px-0.5">|</span>
          )}
          {props.tags?.map((tag, index) => (
            <Badge key={index} variant="ingredient" className="shrink-0">
              {tag}
            </Badge>
          ))}
        </div>
        {props.nutriValues && (
          <>
            {props.showNumbers && (
              <KcalFormLabel kcal={props.nutriValues.kcal} />
            )}
            <NutriBar nutriValues={props.nutriValues} />
            {props.showNumbers && (
              <NutriBullets nutriValues={props.nutriValues} factor={100} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function isRecent(searchResult: MealSourceDto) {
  return differenceInDays(parseDate(searchResult.lastPlanned), today()) <= 7;
}
